import React, {useState, useEffect, useCallback, forwardRef, useMemo} from 'react'
import {Stack, Box, Text, TextInput, Button, Card, Flex, Grid, Select} from '@sanity/ui'
import {set, unset, useFormValue} from 'sanity'
import {useClient} from 'sanity'
import {StringInputProps} from 'sanity'

interface AccessoriesSkusProps extends StringInputProps {
  // Add any additional props if needed
}

interface Product {
  id: number
  name: string
  sku: string
  type: string
  attributes?: Record<string, any>
}

interface SearchFilter {
  code: string
  name: string
  options: Record<string, any>
}

interface FitmentAvailability {
  label: string
  options: any[]
}

const AccessoriesSkus = forwardRef<HTMLInputElement, AccessoriesSkusProps>((props, ref) => {
  const {value, onChange} = props
  const client = useClient()
  const document = useFormValue([]) as any

  // Parse the stored JSON value or initialize as empty array - memoized to prevent infinite loops
  const skus: Product[] = useMemo(() => {
    return typeof value === 'undefined' ? [] : JSON.parse(value || '[]')
  }, [value])

  // State management
  const [query, setQuery] = useState('')
  const [searching, setSearching] = useState(false)
  const [products, setProducts] = useState<Product[]>([])
  const [filteredProducts, setFilteredProducts] = useState<Product[]>([])
  const [fitmentAvailability, setFitmentAvailability] = useState<FitmentAvailability | false>(false)
  const [attributes, setAttributes] = useState<any[]>([])
  const [attributeSets, setAttributeSets] = useState<Record<string, any>>({})
  const [searchFilters, setSearchFilters] = useState<SearchFilter[]>([])
  const [selectedFilters, setSelectedFilters] = useState<Record<string, any>>({})

  // Get frontend URL from environment or fallback (same pattern as AccessorySkusAlt)
  const baseURL = process.env.SANITY_STUDIO_FE_URL || 'http://localhost:3000'

  // Effect to load fitment filter data
  useEffect(() => {
    const fitmentFilter = document?.fitmentFilter || {}
    const _ref = fitmentFilter?._ref || false

    if (_ref) {
      try {
        console.log('Fetching fitment filter for ref:', _ref)
        client.fetch(`*[_id == "${_ref}"][0]{ label, options }`).then((d) => {
          console.log('Fitment filter loaded:', d)
          setFitmentAvailability(d)
        })
      } catch (e) {
        console.error('Error fetching fitment filter:', e)
      }
    } else {
      console.log('No fitment filter reference found, setting to false')
      setFitmentAvailability(false)
    }
  }, [document, client])

  // Filter functions - memoized to prevent unnecessary re-renders
  const filterAlreadyAdded = useCallback(
    (product: Product) => {
      return !skus.map(({sku}) => sku).includes(product.sku)
    },
    [skus],
  )

  const filterProductsByFilters = useCallback(
    (p: Product) => {
      const {attributes} = p
      const filterKeys = Object.keys(selectedFilters).filter((k) => selectedFilters[k] !== null)

      const matchedFilterKeys = filterKeys.filter((k) => {
        const {label} = selectedFilters[k]
        return attributes?.[k]?.value === label
      })

      return filterKeys.length === matchedFilterKeys.length
    },
    [selectedFilters],
  )

  // // Update filtered products when products or filters change
  // useEffect(() => {
  //   const p = products.filter(filterAlreadyAdded).filter(filterProductsByFilters)
  //   setFilteredProducts(p)
  // }, [products, filterAlreadyAdded, filterProductsByFilters])

  // Get attributes for filtering
  const getAttributes = useCallback(async () => {
    try {
      // Using fetch since we can't use axios in the Studio context
      const response = await fetch(`${baseURL}/api/sync/content/data-attributes`, {
        method: 'GET',
      })
      const json = await response.json()
      const {attributes = [], attributeSets = {}} = json?.data || json

      setAttributes(attributes)
      setAttributeSets(attributeSets)

      setSearchFilters([
        {
          code: 'kind',
          name: 'Kind',
          options: Object.keys(attributeSets)
            .filter((k) => {
              return !['Default', 'apex_wheels'].includes(attributeSets[k])
            })
            .reduce(
              (a, k) => ({
                ...a,
                [k]: attributeSets[k],
              }),
              {},
            ),
        },
      ])
    } catch (error) {
      console.error('Error fetching attributes:', error)
    }
  }, [baseURL])

  // Load attributes on mount
  useEffect(() => {
    getAttributes()
  }, [getAttributes])

  // Search products function
  const searchProducts = useCallback(async () => {
    try {
      setSearching(true)
      const kind = selectedFilters?.kind?.value
      const params = new URLSearchParams({
        ...(query.length ? {sku: query} : {}),
        ...(kind ? {attribute_set_id: kind} : {}),
        ...(kind === -1 ? {type_id: 'simple'} : {}),
      })

      const response = await fetch(`${baseURL}/api/callbacks/search-products?${params}`)
      const data = await response.json()

      console.log('Search products data:', data)

      const products = data.map(({id = 0, name = '', sku = '', type = ''}: any) => ({
        id: parseInt(id),
        name,
        sku,
        type,
      }))

      console.log('Search completed, found products:', products.length)
      setProducts(products)
    } catch (e) {
      console.error('Search products error:', (e as Error).message)
    } finally {
      setSearching(false)
    }
  }, [baseURL, query, selectedFilters])

  // Handlers
  const updateQuery = useCallback((event: React.ChangeEvent<HTMLInputElement>) => {
    setQuery(event.target.value)
  }, [])

  const addSku = useCallback(
    (sku: string) => {
      const product = products.find(({sku: _sku}) => _sku === sku)
      if (!product) return

      console.log('Adding product:', product.sku, product.name)

      // Remove from search results
      const updatedProducts = products.filter(({sku: _sku}) => _sku !== sku)
      setProducts(updatedProducts)

      // Add to selected skus
      const updatedSkus = [...skus, product]
      onChange(set(JSON.stringify(updatedSkus)))
    },
    [products, skus, onChange],
  )

  const removeSku = useCallback(
    (index: number) => {
      console.log('Removing product:', skus[index].sku, skus[index].name)
      const updatedSkus = skus.filter((_, i) => i !== index)
      onChange(set(JSON.stringify(updatedSkus)))
    },
    [skus, onChange],
  )

  const updateSearchFilter = useCallback((code: string, value: any) => {
    setSelectedFilters((prev) => ({
      ...prev,
      [code]: value,
    }))
  }, [])

  const clearFilters = useCallback(() => {
    const cleared = Object.keys(selectedFilters).reduce((a, v) => ({...a, [v]: null}), {})
    setSelectedFilters(cleared)
  }, [selectedFilters])

  // Helper functions for native select options
  const getSelectOptions = (obj: Record<string, any>) => {
    const keys = Object.keys(obj || {})
    return keys.map((key) => ({value: key, label: obj[key]}))
  }

  const fitmentAvailabilityOptions = !fitmentAvailability ? [] : fitmentAvailability.options

  // Debug logging effects to prevent render loops
  useEffect(() => {
    console.log('Fitment availability updated:', fitmentAvailability)
  }, [fitmentAvailability])

  useEffect(() => {
    console.log('SKUs updated:', skus)
  }, [skus])

  useEffect(() => {
    console.log('Selected filters updated:', selectedFilters)
  }, [selectedFilters])

  useEffect(() => {
    console.log('Search filters updated:', searchFilters)
  }, [searchFilters])

  return (
    <Stack gap={4}>
      {/* Search Filters */}
      <Card padding={3} tone="transparent">
        <Grid gridTemplateColumns={3} gap={2}>
          {searchFilters.map(({code, name, options}, i) => (
            <Box key={`f${i}`}>
              <Text size={1} weight="medium">
                {name}
              </Text>
              <Box marginTop={1}>
                <Flex gap={2}>
                  <Select
                    value={selectedFilters[code]?.value || ''}
                    onChange={(event) => {
                      const value = event.currentTarget.value
                      const selectedOption = getSelectOptions(options).find(
                        (opt) => opt.value === value,
                      )
                      updateSearchFilter(code, selectedOption || null)
                    }}
                    style={{flex: 1}}
                  >
                    <option value="">Select...</option>
                    {getSelectOptions(options).map((option) => (
                      <option key={option.value} value={option.value}>
                        {option.label}
                      </option>
                    ))}
                  </Select>
                  {selectedFilters[code] && (
                    <Button
                      mode="ghost"
                      tone="default"
                      onClick={() => updateSearchFilter(code, null)}
                      text="Clear"
                     
                    />
                  )}
                </Flex>
              </Box>
            </Box>
          ))}
          <Flex align="flex-end">
            <Button mode="ghost" text="Clear Filters" onClick={clearFilters} fontSize={1} />
          </Flex>
        </Grid>
      </Card>

      {/* Search Container */}
      <Card padding={3} tone="transparent">
        <Flex gap={2}>
          <Box flex={1}>
            <TextInput
              placeholder="Search by SKU..."
              value={query}
              onChange={updateQuery}
              ref={ref}
            />
          </Box>
          <Button
            text={searching ? 'Searching...' : 'Search'}
            onClick={searchProducts}
            disabled={searching}
            tone="primary"
          />
        </Flex>
      </Card>

      {/* Selected SKUs */}
      {skus.length > 0 && (
        <Stack gap={3}>
          <Text size={2} weight="semibold">
            Selected:
          </Text>
          <Stack gap={2}>
            {skus.map((product, i) => (
              <Card key={`sku${i}`} padding={3} tone="primary" border>
                <Flex justify="space-between" align="flex-start">
                  <Stack gap={3}>
                    <Text size={1} weight="medium">
                      {product.name}
                    </Text>
                    <Text size={1} muted>
                      {product.sku}
                    </Text>
                  </Stack>
                  <Button
                    text="Remove"
                    onClick={() => removeSku(i)}
                    mode="ghost"
                    tone="critical"
                    fontSize={1}
                  />
                </Flex>
              </Card>
            ))}
          </Stack>

          <Card padding={3} tone="caution">
            <Text size={1} muted>
              For each selected SKU above, a sku shop page need to exist under Pages / SKUs
            </Text>
          </Card>
        </Stack>
      )}

      {/* Search Results */}
      {products.filter(filterAlreadyAdded).length > 0 && (
        <Stack gap={3}>
          <Text size={2} weight="semibold">
            Results:
          </Text>
          <Stack gap={2}>
            {products.filter(filterAlreadyAdded).map((product, i) => (
              <Card key={`sku${i}`} padding={3} tone="transparent" border>
                <Flex justify="space-between" align="flex-start">
                  <Stack gap={3}>
                    <Text size={1} weight="medium">
                      {product.name}
                    </Text>
                    <Text size={1} muted>
                      {product.sku}
                    </Text>
                  </Stack>
                  <Button
                    text="Add"
                    onClick={() => addSku(product.sku)}
                    mode="default"
                    fontSize={1}
                  />
                </Flex>
              </Card>
            ))}
          </Stack>
        </Stack>
      )}
    </Stack>
  )
})

AccessoriesSkus.displayName = 'AccessoriesSkus'

export default AccessoriesSkus
